const BASE_URL = '//' + window.skyline.env.BASE_URL
const MISSIONS = '/missions'
const DEVICES = '/devices'

import * as adminFeed from "./adminFeed"

// Auth parts
let TOKEN
let tokenExp
let currentUserName = 'Observer'


const deviceCache = {
  devices: {},
  lastLoaded: 0,
  ttl: 60 * 1000, // reload time in millis
}

const missionCache = {
  missions: {},
  lastLoaded: 0,
  ttl: 60 * 1000, // reload time in millis
}

window.deviceCache = deviceCache
window.missionCache = missionCache

adminFeed.register((ev) => {
  let cache
  switch (ev.entityType) {
  case "mission":
    cache = missionCache.missions
    break
  case "device":
    cache = deviceCache.devices
    break
  default:
    console.error("Unexpected type on admin feed", ev)
    return
  }

  const entity = ev[ev.entityType]
  switch (ev.action) {
  case "CREATE":
    if (cache[entity.id]) {
      console.error("Already have a created entity", entity)
    }
  case "UPDATE":
    cache[entity.id] = entity
    break
  case "DELETE":
    delete cache[entity.id]
    break
  default:
    console.error("Unexpected action on admin feed.", ev)
    return
  }
})

export function getDevice(id) {
  return deviceCache.devices[id]
}

export function getDevices() {
  return deviceCache.devices
}

export async function loadDevices() {
  const token = await getToken()
  const devices_resp = await fetch(`${BASE_URL}${DEVICES}`, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`
    }
  })

  if (!devices_resp.ok) {
    throw new Error('Could not get the device list: ' + await getErrorFromResponse(devices_resp))
  }

  const ret = await devices_resp.json()

  for (const d of ret) {
    if (deviceCache.devices[d.id]) {
      Object.assign(deviceCache.devices[d.id], d)
    } else {
      deviceCache.devices[d.id] = d
    }
  }
  deviceCache.lastLoaded = Date.now()
}

export function getMission(id) {
  return missionCache.missions[id]
}

export function getMissions() {
  return missionCache.missions
}

export async function loadMissions() {
  const token = await getToken()
  const missions_resp = await fetch(`${BASE_URL}${MISSIONS}`, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`
    }
  })
  if (missions_resp.ok) {
    const missions = await missions_resp.json()
    missions.reverse()
    missionCache.missions = missions.reduce((object, item) => {
      object[item.id] = item
      return object
    }, {})
  } else {
    throw new Error('Could not get missions: ' + await getErrorFromResponse(missions_resp));
  }
}

export async function getErrorFromResponse(response) {
  let errorMessage = response.statusText
  try {
    let resp = await response.json()
    errorMessage = resp.message || resp.error || resp
  } catch (error) {
    // ignore
  }
  return errorMessage
}

export async function updateDevice(id, device) {
  const token = await getToken()
  const req = await fetch(`${BASE_URL}${DEVICES}/${id}`, {
    method: "PATCH",
    headers: {
      "Authorization": `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(device),
  })
  if (req.ok) {
    const device = await req.json()
    if (deviceCache.devices[id]) {
      Object.assign(deviceCache.devices[id], device)
    } else {
      deviceCache.devices[id] = device
    }
    return device
  } else {
    const message = await getErrorFromResponse(req)
    throw new Error("There was an error saving the device: " + message)
  }
}

export async function createDevice(device) {
  const token = await getToken()
  const req = await fetch(`${BASE_URL}${DEVICES}`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(device),
  })
  if (req.ok) {
    const device = await req.json()
    if (!deviceCache.devices[device.id]) deviceCache.devices[device.id] = device
    return device
  } else {
    const message = await getErrorFromResponse(req)
    throw new Error("There was an error creating the device: " + message)
  }
}

export async function deleteDevice(device) {
  const token = await getToken()
  const req = await fetch(`${BASE_URL}${DEVICES}/${device.id}`, {
    method: "DELETE",
    headers: {
      "Authorization": `Bearer ${token}`
    }
  })
  if (req.ok) {
    delete deviceCache.devices[device.id]
  } else {
    const message = await getErrorFromResponse(req)
    throw new Error("There was an error deleting the device: " + message)
  }
}

export async function startMission(mission) {
  const token = await getToken()
  let start_req = await fetch(`${BASE_URL}${MISSIONS}/${mission.id}/start`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${token}`
    }
  })
  console.log("Start req:", start_req)
  if (start_req.ok) {
    return await start_req.json()
  } else {
    const message = await getErrorFromResponse(start_req)
    throw new Error("There was an error starting the mission: " + message)
  }
}

export async function stopMission(mission) {
  const token = await getToken()
  let stop_req = await fetch(`${BASE_URL}${MISSIONS}/${mission.id}/complete`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${token}`
    }
  })
  console.log("Stop req:", stop_req)
  if (stop_req.ok) {
    let resp = await stop_req.json()
    mission.state = resp.state
    return resp
  } else {
    throw new Error("There was an error ending the mission: " + await getErrorFromResponse(stop_req))
  }
}

export async function createMission(details) {
  const token = await getToken()
  let create_req = await fetch(`${BASE_URL}${MISSIONS}`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      name: details.name,
      grsDeviceIds: details.grses,
      arsDeviceId: details.ars,
      bounds: details.bounds
    })
  })
  if (!create_req.ok) {
    throw new Error('Could not create the mission: ' + await getErrorFromResponse(create_req))
  } else {
    let info = await create_req.json()
    if (!missionCache.missions[info.id]) {
      missionCache.missions[info.id] = info
    }
    return info
  }
}

export async function getMissionLog(id) {
  const token = await getToken()
  let response = await fetch(`${BASE_URL}/active_mission/${id}/log`, {
    withCredentials: true,
    headers: {
      "Authorization": `Bearer ${token}`
    }
  })
  if (!response.ok) {
    throw new Error('Could not get mission log file: ' + await getErrorFromResponse(response))
  }
  const messages = []
  for await (let line of makeStreamLineIterator(response.body)) {
    try {
      const message = JSON.parse(line)
      messages.push(message)
    } catch (error) {
      console.error('Could not parse log line', line)
    }
  }

  return messages
}

async function* makeStreamLineIterator(stream) {
  const utf8Decoder = new TextDecoder("utf-8");
  let reader = stream.getReader();
  let {value: chunk, done: readerDone} = await reader.read();
  chunk = chunk ? utf8Decoder.decode(chunk, {stream: true}) : "";

  let re = /\r\n|\n|\r/gm;
  let startIndex = 0;

  for (;;) {
    let result = re.exec(chunk);
    if (!result) {
      if (readerDone) {
        break;
      }
      let remainder = chunk.substr(startIndex);
      ({value: chunk, done: readerDone} = await reader.read());
      chunk = remainder + (chunk ? utf8Decoder.decode(chunk, {stream: true}) : "");
      startIndex = re.lastIndex = 0;
      continue;
    }
    yield chunk.substring(startIndex, result.index);
    startIndex = re.lastIndex;
  }
  if (startIndex < chunk.length) {
    // last line didn't end in a newline char
    yield chunk.substr(startIndex);
  }
}

export async function getToken(userName) {
  if (TOKEN && tokenExp && tokenExp > (Date.now() / 1000) && (userName === currentUserName || userName === undefined)) {
    return TOKEN
  }
  currentUserName = userName || "Observer"
  tokenExp = null

  console.log("Getting token as ", currentUserName)
  const auth_url = window.location.protocol + `//${window.skyline.env.BASE_URL}/auth`
  const token_req = await fetch(auth_url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      "client_id": currentUserName,
      "secret": "mnoc_secret"
    })
  })
  if (!token_req.ok) throw new Error('Could not get auth token: ' + await getErrorFromResponse(token_req))
  const bearer_info = await token_req.json()
  TOKEN = bearer_info.token
  let userInfo
  try {
    userInfo = jwtDecode(TOKEN)
    tokenExp = userInfo.exp
  } catch (error) {
    console.error('Could not parse JWT', error)
  }
  return TOKEN
}

export function userHasRole(userInfo, role) {
  return userInfo && userInfo.scope && userInfo.scope.includes(';' + role + ';')
}

export function getUser() {
  if (!TOKEN) return
  return jwtDecode(TOKEN)
}

function jwtDecode(token) {
  // https://stackoverflow.com/a/38552302/243953
  var base64Url = token.split('.')[1];
  var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  var jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(''));

  return JSON.parse(jsonPayload);
}
